import { WSQueryCache } from "@ws-react-query";
import {
  IPayeeCreateRequest,
  IPayeeResponse
} from "@wingspanhq/payments/dist/interfaces";
import { QUERY_PAYEE } from "../../../query/payee/keys";
import {
  QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
  QUERY_PAYEE_ENGAGEMENTS_LIST,
  QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL,
  QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE
} from "../../../query/payeeEngagements/keys";
import { QUERY_PAYEE_ROWS } from "../../../query/search/payee/queries/useQueryPayeeRows";
import { QUERY_PAYEE_ROWS_SUMMARY } from "../../../query/search/payee/queries/useQueryPayeeRowsSummary";
import { createPayee } from "../../../services/payees";
import { createPayeeEngagementByPayeeId } from "../../../services/payeeEngagement/createPayeeEngagementByPayeeId";
import {
  bulkActionFactory,
  BulkActionProgress,
  useForegroundBulkAction
} from "../../BulkActions";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

export interface CreateContractorBulkEngagement {
  id: string;
  name: string;
}

export interface CreateContractorBulkActionInput {
  payeeCreationData: Omit<IPayeeCreateRequest, "engagementId">;
  engagements: CreateContractorBulkEngagement[];
}

export interface CreateContractorBulkActionSettings {
  toDetailsOnSuccess: boolean;
}

type Response = {
  payee?: IPayeeResponse;
  toDetails?: boolean;
};

export const useCreateContractorBulkAction = (): ((
  input: CreateContractorBulkActionInput,
  settings: CreateContractorBulkActionSettings
) => Promise<Response>) => {
  const runBulkAction = useForegroundBulkAction();
  const xWingspanPayerId = useXWingspanPayerId();

  return async ({ payeeCreationData, engagements }, { toDetailsOnSuccess }) => {
    const total = 1 + engagements.length;
    const response: Response = {};

    await runBulkAction({
      actionName: "Add contractor and assignments",
      itemCount: total,
      skipConfirm: true,
      nextSecondaryAction: {
        label: toDetailsOnSuccess ? "Close" : "Continue"
      },
      nextAction: toDetailsOnSuccess
        ? {
            label: "Go to details",
            onClick() {
              response.toDetails = true;
            }
          }
        : undefined,
      prepare: async () => ({ payeeCreationData, engagements }),
      process: async (data, onProgress): Promise<BulkActionProgress> => {
        onProgress({
          total,
          processed: 0,
          successful: 0,
          failed: 0,
          progressMessage: "Creating contractor..."
        });

        try {
          const payee = await createPayee(data.payeeCreationData);
          WSQueryCache.setQueryData([QUERY_PAYEE, payee.payeeId, null], payee);
          response.payee = payee;
        } catch {
          onProgress({
            total,
            processed: 1,
            successful: 0,
            failed: 1,
            progressMessage: "Failed to create contractor",
            failedDescriptions: ["Create contractor"]
          });
          return {
            total,
            processed: 1,
            successful: 0,
            failed: 1,
            failedDescriptions: ["Create contractor"]
          };
        }

        const contractorSuccessDescriptions = ["Contractor created"];

        onProgress({
          total,
          processed: 1,
          successful: 1,
          failed: 0,
          progressMessage: "Contractor created",
          successDescriptions: contractorSuccessDescriptions
        });

        const bulk = bulkActionFactory<CreateContractorBulkEngagement>({
          action: engagement =>
            createPayeeEngagementByPayeeId(
              response.payee!.payeeId,
              { engagementId: engagement.id, payerOwnedData: {} },
              xWingspanPayerId
            ),
          getSuccessDescription: engagement => `Assigned "${engagement.name}"`,
          getFailedDescription: engagement => `Assign "${engagement.name}"`,
          getProgressMessage: (_, progress) =>
            `Assigning engagements (${progress.processed} of ${progress.total})`,
          onProgress: progress =>
            onProgress({
              total,
              processed: 1 + progress.processed,
              successful: 1 + progress.successful,
              failed: progress.failed,
              progressMessage: progress.progressMessage,
              successDescriptions: [
                ...contractorSuccessDescriptions,
                ...(progress.successDescriptions ?? [])
              ],
              failedDescriptions: progress.failedDescriptions
            }),
          onFinish: () => {
            WSQueryCache.invalidateQueries(QUERY_PAYEE_ROWS);
            WSQueryCache.invalidateQueries(QUERY_PAYEE_ROWS_SUMMARY);
            WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID);
            WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENTS_LIST);
            WSQueryCache.invalidateQueries(
              QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL
            );
            WSQueryCache.invalidateQueries(QUERY_PAYEE_ENGAGEMENTS_LIST_SIZE);
            WSQueryCache.invalidateQueries(QUERY_PAYEE);
          }
        });

        const { successItems, failedItems } = await bulk.run(data.engagements);

        return {
          total,
          processed: 1 + successItems.length + failedItems.length,
          successful: 1 + successItems.length,
          failed: failedItems.length,
          successDescriptions: [
            ...contractorSuccessDescriptions,
            ...successItems.map(e => `Assigned "${e.name}"`)
          ],
          failedDescriptions: failedItems.map(e => `Assign "${e.name}"`)
        };
      }
    });

    return response;
  };
};
